import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Typography, 
  Button, 
  Space, 
  Form,
  Row,
  Col,
  Input,
  Select,
  Switch,
  message,
  Spin,
  Modal
} from 'antd';
import { 
  ArrowLeftOutlined,
  PlayCircleOutlined,
  EditOutlined,
  SaveOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { adminSentenceApi } from '@/services/api';

const { Title } = Typography;
const { TextArea } = Input;

interface SentenceDetail {
  id: string;
  sentence: string;
  ttsGender: string;
  ttsAge: string;
  ttsSpeed: string;
  ttsPitch: string;
  isPublic: boolean;
  registrant: string;
  registrationDate: string;
}

const AdminSentenceDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sentenceId } = useParams<{ sentenceId: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sentenceData, setSentenceData] = useState<SentenceDetail | null>(null);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [learningHistoryModalVisible, setLearningHistoryModalVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const autoPreviewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [originalSentence, setOriginalSentence] = useState<string | null>(null);
  const [isSentenceChanged, setIsSentenceChanged] = useState<boolean>(false);
  const [originalTtsGender, setOriginalTtsGender] = useState<string | null>(null);
  const [originalTtsAge, setOriginalTtsAge] = useState<string | null>(null);
  const [originalTtsSpeed, setOriginalTtsSpeed] = useState<string | null>(null);
  const [originalTtsPitch, setOriginalTtsPitch] = useState<string | null>(null);
  const [isTtsOptionChanged, setIsTtsOptionChanged] = useState<boolean>(false);
  const [usageCount, setUsageCount] = useState<number>(0);
  const [pendingSubmitValues, setPendingSubmitValues] = useState<any | null>(null);
  const fromPath = (location.state as { from?: string } | null)?.from;

  const fetchUsageCount = async () => {
    if (!sentenceId) return 0;
    try {
      const res: any = await adminSentenceApi.usage(Number(sentenceId));
      const data = res?.data || res;
      const count = data?.usageCount ?? 0;
      setUsageCount(count);
      return count;
    } catch (e) {
      console.error('fetchUsageCount failed', e);
      return -1;
    }
  };

  const isEditMode = !!sentenceId; // sentenceId가 있으면 수정 모드

  const handleBackToList = () => {
    navigate(fromPath || '/admin/sentence');
  };

  // TTS 옵션들 (더미 데이터 - 실제로는 API에서 가져와야 함)
  const genderOptions = [
    { value: 'male', label: '남성' },
    { value: 'female', label: '여성' }
  ];

  const ageOptions = [
    { value: 'child', label: '아동' },
    { value: 'young', label: '청소년' },
    { value: 'adult', label: '성인' },
    { value: 'elder', label: '노년' }
  ];

  const speedOptions = [
    { value: 'slow', label: '느림' },
    { value: 'normal', label: '보통' },
    { value: 'fast', label: '빠름' }
  ];

  const pitchOptions = [
    { value: 'low', label: '낮음' },
    { value: 'medium', label: '보통' },
    { value: 'high', label: '높음' }
  ];

  // 샘플 데이터 (수정 모드일 때 사용)
  const sampleSentenceData: SentenceDetail = {
    id: 'SEN-001',
    sentence: '안녕하세요. 오늘 날씨가 정말 좋네요.',
    ttsGender: 'female',
    ttsAge: 'adult',
    ttsSpeed: 'normal',
    ttsPitch: 'medium',
    isPublic: true,
    registrant: '김관리자',
    registrationDate: '2024-01-15'
  };

  const handlePreview = async (opts?: { silent?: boolean }) => {
    const sentence = form.getFieldValue('sentence');
    const gender = form.getFieldValue('ttsGender');
    const age = form.getFieldValue('ttsAge');
    const speed = form.getFieldValue('ttsSpeed');
    const pitch = form.getFieldValue('ttsPitch');
    if (!sentence || sentence.trim() === '') {
      if (!opts?.silent) {
        message.warning('문장을 입력해주세요.');
      }
      return;
    }
    try {
      const blob = await adminSentenceApi.preview({ sentence, gender, age, speed, pitch });
      const url = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setTimeout(() => {
        audioRef.current?.play().catch(() => {
          message.warning('브라우저에서 자동 재생이 차단되었습니다. 플레이 버튼을 눌러주세요.');
        });
      }, 0);
    } catch (e) {
      message.error('미리듣기 생성에 실패했습니다.');
    }
  };

  const scheduleAutoPreview = () => {
    if (autoPreviewTimerRef.current) {
      clearTimeout(autoPreviewTimerRef.current);
    }
    autoPreviewTimerRef.current = setTimeout(() => {
      handlePreview({ silent: true });
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (autoPreviewTimerRef.current) clearTimeout(autoPreviewTimerRef.current);
    };
  }, [previewUrl]);

  const handleSubmit = async (values: any) => {
    setSaving(true);
    try {
      if (isEditMode) {
        if (isSentenceChanged || isTtsOptionChanged) {
          const latestUsage = await fetchUsageCount();
          if (latestUsage > 0) {
            setPendingSubmitValues(values);
            setLearningHistoryModalVisible(true);
            setSaving(false);
            return;
          } else if (latestUsage === -1) {
             message.warning('사용 이력 확인 중 오류가 발생했습니다.');
          }
        }
        const payload = {
          sentence: values.sentence,
          translation: values.translation,
          gender: values.ttsGender,
          age: values.ttsAge,
          speed: values.ttsSpeed,
          pitch: values.ttsPitch,
          isPublic: values.isPublic,
        };
        const idNum = Number(sentenceId);
        const res = await adminSentenceApi.update(idNum, payload);
        if (res?.success) {
          message.success('문장이 성공적으로 수정되었습니다.');
          setSentenceData({ ...sentenceData!, ...values });
          setOriginalSentence(values.sentence);
          setOriginalTtsGender(values.ttsGender);
          setOriginalTtsAge(values.ttsAge);
          setOriginalTtsSpeed(values.ttsSpeed);
          setOriginalTtsPitch(values.ttsPitch);
          setIsSentenceChanged(false);
          setIsTtsOptionChanged(false);
          setPreviewUrl(null);
          if (res.data?.audioUrl) setAudioUrl(res.data.audioUrl);
        } else {
          message.error('문장 수정에 실패했습니다.');
        }
      } else {
        const payload = {
          sentence: values.sentence,
          translation: values.translation,
          gender: values.ttsGender,
          age: values.ttsAge,
          speed: values.ttsSpeed,
          pitch: values.ttsPitch,
          isPublic: values.isPublic,
          registrant: values.registrant,
        };
        const res = await adminSentenceApi.create(payload);
        if (res?.success) {
          setSuccessModalVisible(true);
        } else if (res?.message?.includes('중복')) {
          setDuplicateModalVisible(true);
        } else {
          message.error('문장 등록에 실패했습니다.');
        }
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || '';
      if (msg.includes('중복')) {
        setDuplicateModalVisible(true);
        if (msg.includes('tts유형까지 동일')) {
          message.warning('tts유형까지 동일한 문장은 중복 등록할 수 없습니다.');
        }
      } else if (isEditMode && msg.includes('forceUpdate')) {
        setPendingSubmitValues(values);
        setLearningHistoryModalVisible(true);
        return;
      } else {
        message.error(isEditMode ? '문장 수정에 실패했습니다.' : '문장 등록에 실패했습니다.');
      }
    } finally {
      setSaving(false);
      setPendingSubmitValues(null);
    }
  };

  const handleLearningHistoryConfirm = async () => {
    setLearningHistoryModalVisible(false);
    setSaving(true);
    try {
      const values = pendingSubmitValues || form.getFieldsValue();
      const payload = {
        sentence: values.sentence,
        translation: values.translation,
        gender: values.ttsGender,
        age: values.ttsAge,
        speed: values.ttsSpeed,
        pitch: values.ttsPitch,
        isPublic: values.isPublic,
        forceUpdate: true,
      };
      const idNum = Number(sentenceId);
      const res = await adminSentenceApi.update(idNum, payload);
      if (res?.success) {
        message.success('문장이 성공적으로 수정되었습니다.');
        setSentenceData({ ...sentenceData!, ...values });
        setOriginalSentence(values.sentence);
        setOriginalTtsGender(values.ttsGender);
        setOriginalTtsAge(values.ttsAge);
        setOriginalTtsSpeed(values.ttsSpeed);
        setOriginalTtsPitch(values.ttsPitch);
        setIsSentenceChanged(false);
        setIsTtsOptionChanged(false);
        setPreviewUrl(null);
        if (res.data?.audioUrl) setAudioUrl(res.data.audioUrl);
      } else {
        message.error('문장 수정에 실패했습니다.');
      }
    } catch (error) {
      message.error('문장 수정에 실패했습니다.');
    } finally {
      setSaving(false);
      setPendingSubmitValues(null);
    }
  };

  const handleLearningHistoryCancel = () => {
    setLearningHistoryModalVisible(false);
    setSaving(false);
    setPendingSubmitValues(null);
  };

  const handleSuccessModalOk = () => {
    setSuccessModalVisible(false);
    navigate('/admin/sentence');
  };

  const handleDuplicateModalOk = () => {
    setDuplicateModalVisible(false);
  };

  const handleCancel = () => {
    navigate('/admin/sentence');
  };

  useEffect(() => {
    if (isEditMode) {
      // 수정 모드: 데이터 로드 (서버)
      setLoading(true);
      (async () => {
        try {
          const res = await adminSentenceApi.detail(sentenceId);
          if (res?.success) {
            const d = res.data;
            const mapped = {
              id: d.code || `SEN-${String(d.id).padStart(4, '0')}`,
              sentence: d.text,
              translation: d.translation,
              ttsGender: d.gender,
              ttsAge: d.age,
              ttsSpeed: d.speed,
              ttsPitch: d.pitch,
              isPublic: d.isPublic,
              registrant: d.registrant || '-',
              registrationDate: (d.createdAt || '').split('T')[0] || ''
            } as SentenceDetail;
            setSentenceData(mapped);
            setAudioUrl(d.audioUrl || null);
            setOriginalSentence(d.text || '');
            setOriginalTtsGender(d.gender || '');
            setOriginalTtsAge(d.age || '');
            setOriginalTtsSpeed(d.speed || '');
            setOriginalTtsPitch(d.pitch || '');
            setUsageCount(d.usageCount || 0);
            setIsSentenceChanged(false);
            setIsTtsOptionChanged(false);
            form.setFieldsValue(mapped);
          } else {
            message.error('상세 정보를 불러오지 못했습니다.');
          }
        } catch (e) {
          message.error('상세 정보를 불러오지 못했습니다.');
        } finally {
          setLoading(false);
        }
      })();
    } else {
      // 등록 모드: 빈 폼으로 시작
      form.setFieldsValue({
        isPublic: true,
        registrant: '관리자', // 실제로는 로그인한 사용자 정보
        ttsGender: 'female',
        ttsAge: 'adult',
        ttsSpeed: 'normal',
        ttsPitch: 'medium'
      });
    }
  }, [sentenceId, form, isEditMode]);

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: '24px', textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" tip="로딩 중..." />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Space style={{ marginBottom: 16 }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBackToList}
            >
              목록으로
            </Button>
          </Space>
          <Title level={2}>
            {isEditMode ? '문장 상세' : '문장 등록'}
          </Title>
          {isEditMode && sentenceData && (
            <div style={{ color: '#666' }}>문장코드: {sentenceData.id}</div>
          )}
        </div>

        {/* 폼 */}
        
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={(changed, all) => {
              if (Object.prototype.hasOwnProperty.call(changed, 'sentence')) {
                const cur = (changed as any).sentence ?? '';
                const base = originalSentence ?? '';
                const changedFlag = cur !== base;
                setIsSentenceChanged(changedFlag);
                // 원문으로 되돌아가면 미리듣기 URL 제거 (저장된 음원 재생)
                if (!changedFlag && !isTtsOptionChanged) setPreviewUrl(null);
              }
              const ttsFieldChanged =
                Object.prototype.hasOwnProperty.call(changed, 'ttsGender') ||
                Object.prototype.hasOwnProperty.call(changed, 'ttsAge') ||
                Object.prototype.hasOwnProperty.call(changed, 'ttsSpeed') ||
                Object.prototype.hasOwnProperty.call(changed, 'ttsPitch');
              const curGender = (all as any).ttsGender ?? '';
              const curAge = (all as any).ttsAge ?? '';
              const curSpeed = (all as any).ttsSpeed ?? '';
              const curPitch = (all as any).ttsPitch ?? '';
              const baseGender = originalTtsGender ?? '';
              const baseAge = originalTtsAge ?? '';
              const baseSpeed = originalTtsSpeed ?? '';
              const basePitch = originalTtsPitch ?? '';
              const ttsChanged = curGender !== baseGender || curAge !== baseAge || curSpeed !== baseSpeed || curPitch !== basePitch;
              setIsTtsOptionChanged(ttsChanged);
              if (!ttsChanged && !isSentenceChanged) setPreviewUrl(null);
              if (ttsFieldChanged) {
                scheduleAutoPreview();
              }
            }}
          >
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  label="문장"
                  name="sentence"
                  rules={[
                    { required: true, message: '문장을 입력해주세요' },
                    { min: 1, max: 500, message: '문장은 1~500자로 입력해주세요' }
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="문장을 입력하세요"
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  label="해석"
                  name="translation"
                  rules={[{ required: true, message: '해석을 입력해주세요' }]}
                >
                  <Input
                    placeholder="해석을 입력하세요"
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Card style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 12 }}>TTS 설정</p>

            <Row gutter={20}>
              <Col span={4}>
                <Form.Item
                  label="음성성별"
                  name="ttsGender"
                  rules={[
                    { required: true, message: '음성성별을 선택해주세요' }
                  ]}
                >
                  <Select
                    placeholder="음성성별을 선택하세요"
                    size="large"
                    options={genderOptions}
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="연령대"
                  name="ttsAge"
                  rules={[
                    { required: true, message: '연령대를 선택해주세요' }
                  ]}
                >
                  <Select
                    placeholder="연령대를 선택하세요"
                    size="large"
                    options={ageOptions}
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="속도"
                  name="ttsSpeed"
                  rules={[
                    { required: true, message: '속도를 선택해주세요' }
                  ]}
                >
                  <Select
                    placeholder="속도를 선택하세요"
                    size="large"
                    options={speedOptions}
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="피치"
                  name="ttsPitch"
                  rules={[
                    { required: true, message: '피치를 선택해주세요' }
                  ]}
                >
                  <Select
                    placeholder="피치를 선택하세요"
                    size="large"
                    options={pitchOptions}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="미리듣기">
                  <Button 
                    icon={<PlayCircleOutlined />} 
                    type="primary" 
                    ghost
                    onClick={() => handlePreview()}
                    size="large"
                    style={{ width: '100%', height: 40, fontSize: 16 }}
                    disabled={isEditMode && !(isSentenceChanged || isTtsOptionChanged)}
                  >
                    미리듣기
                  </Button>
                </Form.Item>
              </Col>
            </Row>
            </Card>

            {(previewUrl || audioUrl) && (
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 14, marginBottom: 8 }}>미리듣기</p>
                <audio ref={audioRef} controls src={previewUrl || audioUrl || undefined} style={{ width: '100%' }} preload="auto" />
              </div>
            )}

            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  label="공개설정"
                  name="isPublic"
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren="공개"
                    unCheckedChildren="비공개"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
            <Col span={8}>
                <Form.Item
                  label="등록자"
                  name="registrant"
                >
                  <Input
                    disabled
                    size="large"
                  />
                </Form.Item>
              </Col>
              {isEditMode && sentenceData && (
                <Col span={8}>
                  <Form.Item
                    label="등록일"
                  >
                    <Input
                      value={sentenceData.registrationDate}
                      disabled
                      size="large"
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
            {/* 버튼 영역 */}
            <div style={{ textAlign: 'right', marginTop: 32 }}>
              <Space size="middle">
                <Button 
                  onClick={handleCancel}
                  size="large"
                  style={{ height: 48, fontSize: 16, padding: '0 32px' }}
                >
                  취소
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={saving}
                  style={{ height: 48, fontSize: 16, padding: '0 32px' }}
                >
                  {isEditMode ? '수정' : '등록'}
                </Button>
              </Space>
            </div>
          </Form>
    

        {/* 학습이력 확인 모달 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 20 }} />
              <span>문장 수정</span>
            </div>
          }
          open={learningHistoryModalVisible}
          onOk={handleLearningHistoryConfirm}
          onCancel={handleLearningHistoryCancel}
          footer={[
            <Button key="cancel" onClick={handleLearningHistoryCancel}>
              취소
            </Button>,
            <Button key="confirm" type="primary" onClick={handleLearningHistoryConfirm}>
              확인
            </Button>
          ]}
          centered
          width={500}
        >
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 16, marginBottom: 8, fontWeight: 'bold' }}>
              작성이력 있는 문장입니다. 수정하시겠습니까?
            </div>
          </div>
        </Modal>

        {/* 성공 모달 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
              <span>등록 완료</span>
            </div>
          }
          open={successModalVisible}
          onOk={handleSuccessModalOk}
          onCancel={handleSuccessModalOk}
          footer={[
            <Button key="ok" type="primary" onClick={handleSuccessModalOk}>
              확인
            </Button>
          ]}
          centered
          width={500}
        >
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 16, marginBottom: 8, fontWeight: 'bold' }}>
              문장 등록 및 음원 생성이 완료되었습니다.
            </div>
            <div style={{ color: '#666', fontSize: 14 }}>
              등록된 문장은 문장 리스트에서 확인할 수 있습니다.
            </div>
          </div>
        </Modal>

        {/* 중복 문장 모달 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 20 }} />
              <span>중복 문장</span>
            </div>
          }
          open={duplicateModalVisible}
          onOk={handleDuplicateModalOk}
          onCancel={handleDuplicateModalOk}
          footer={[
            <Button key="ok" type="primary" onClick={handleDuplicateModalOk}>
              확인
            </Button>
          ]}
          centered
          width={500}
        >
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 16, marginBottom: 8, fontWeight: 'bold' }}>
              이미 등록된 문장입니다.
            </div>
            <div style={{ color: '#666', fontSize: 14 }}>
              동일한 문장이 이미 등록되어 있습니다. 다른 문장을 입력해주세요.
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default AdminSentenceDetail; 